import React, { useState } from 'react';
import {
  X, AlertTriangle, Shield, Bug, Server, Terminal, ExternalLink, Copy,
  Zap, Target, Clock, CheckCircle, XCircle, Image, Code, FileText,
  ChevronDown, ChevronUp, Activity, Lock, Unlock, Eye, Download
} from 'lucide-react';
import { useI18n } from '../i18n';

const VulnerabilitySlider = ({ vulnerability, onClose, isOpen }) => {
  const { t } = useI18n();
  const [expandedSections, setExpandedSections] = useState({
    exploitation: true,
    details: true,
    attack: true,
    proof: true,
    solution: true
  });

  if (!isOpen || !vulnerability) return null;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/50',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      low: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      info: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
    };
    return colors[severity?.toLowerCase()] || colors.info;
  };

  const getSeverityBg = (severity) => {
    const colors = {
      critical: 'from-red-600 to-red-800',
      high: 'from-orange-600 to-orange-800',
      medium: 'from-yellow-600 to-yellow-800',
      low: 'from-blue-600 to-blue-800',
    };
    return colors[severity?.toLowerCase()] || 'from-gray-600 to-gray-800';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Mock data for attack details if not present
  const attackDetails = vulnerability.attack_details || {
    vector: vulnerability.attack_vector || 'Network',
    complexity: vulnerability.attack_complexity || 'Low',
    privileges: vulnerability.privileges_required || 'None',
    interaction: vulnerability.user_interaction || 'None',
    scope: 'Changed',
    impact_confidentiality: 'High',
    impact_integrity: 'High',
    impact_availability: 'High'
  };

  // Mock proof/evidence data
  const proofData = vulnerability.proof || {
    tool_used: vulnerability.tool || 'Nmap + Nuclei',
    command: vulnerability.command || `nmap -sV --script vuln ${vulnerability.host_ip || 'target'}`,
    output: vulnerability.raw_output || `[+] Vulnerability detected on port ${vulnerability.port || '80'}
[*] Service: ${vulnerability.service || 'HTTP'}
[!] ${vulnerability.title || 'Security Issue'} confirmed
[*] CVE: ${vulnerability.cve_id || 'N/A'}`,
    screenshot: vulnerability.screenshot_url || null,
    timestamp: vulnerability.discovered_at || new Date().toISOString()
  };

  // Solution/remediation data
  const solution = {
    description: vulnerability.remediation || vulnerability.solution || 'Apply the latest security patches and follow vendor recommendations.',
    steps: vulnerability.remediation_steps || [
      'Update the affected software to the latest version',
      'Apply vendor security patches',
      'Review and harden configuration settings',
      'Implement network segmentation if applicable',
      'Monitor for suspicious activities'
    ],
    references: vulnerability.references || [
      { title: vulnerability.cve_id || 'CVE Reference', url: `https://nvd.nist.gov/vuln/detail/${vulnerability.cve_id || 'CVE-0000-0000'}` },
      { title: 'Avis du fournisseur', url: '#' }
    ],
    workaround: vulnerability.workaround || 'If the patch cannot be applied immediately, consider implementing firewall rules to restrict access to the affected service.'
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slider Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-brand-black-light border-l border-brand-green/30 z-50 overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className={`bg-gradient-to-r ${getSeverityBg(vulnerability.severity)} p-6`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getSeverityColor(vulnerability.severity)} bg-black/30`}>
                  {t(`vulnerabilities.severities.${vulnerability.severity}`)}
                </span>
                {vulnerability.cvss_score && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-black/30 text-white border border-white/30">
                    CVSS: {vulnerability.cvss_score}
                  </span>
                )}
                {(vulnerability.exploitation_successful || vulnerability.exploited) && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-purple-600/50 text-white border border-purple-400/50 animate-pulse whitespace-nowrap flex-shrink-0">
                    <Zap className="w-3 h-3 flex-shrink-0" />
                    EXPLOITED ✓
                  </span>
                )}
                {vulnerability.validation_status === 'confirmed_exploitable' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-red-600/50 text-red-300 border border-red-400/50 whitespace-nowrap flex-shrink-0">
                    <CheckCircle className="w-3 h-3 flex-shrink-0" />
                    {t('vulnerabilities.validation.confirmed')}
                  </span>
                )}
                {vulnerability.validation_status === 'theoretical' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-yellow-600/40 text-yellow-300 border border-yellow-400/50 whitespace-nowrap flex-shrink-0">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    {t('vulnerabilities.validation.theoretical')}
                  </span>
                )}
                {vulnerability.validation_status === 'not_exploitable' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-600/40 text-green-300 border border-green-400/50 whitespace-nowrap flex-shrink-0">
                    <Shield className="w-3 h-3 flex-shrink-0" />
                    {t('vulnerabilities.validation.not_exploitable')}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{vulnerability.title}</h2>
              {vulnerability.cve_id && (
                <div className="flex items-center gap-2">
                  <span className="text-white/80 font-mono text-sm">{vulnerability.cve_id}</span>
                  <button
                    onClick={() => copyToClipboard(vulnerability.cve_id)}
                    className="p-1 hover:bg-white/20 rounded"
                    title="Copy CVE ID"
                  >
                    <Copy className="w-3 h-3 text-white/60" />
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Quick Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                <Server className="w-3 h-3" />
                Host
              </div>
              <span className="text-white font-mono text-sm">{vulnerability.host_ip || vulnerability.host || 'N/A'}</span>
            </div>
            <div className="bg-brand-black rounded-lg p-3 border border-gray-800">
              <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                <Terminal className="w-3 h-3" />
                Port/Service
              </div>
              <span className="text-white text-sm">{vulnerability.port || 'N/A'} / {vulnerability.service || 'Unknown'}</span>
            </div>
          </div>

          {/* Exploitation Section - Only show if exploitation was attempted */}
          {(vulnerability.exploitation_successful || vulnerability.exploitation_attempted) && (
            <div className="bg-brand-black rounded-xl border border-purple-500/50 overflow-hidden">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/50 bg-purple-500/10"
                onClick={() => toggleSection('exploitation')}
              >
                <h3 className="text-purple-400 font-bold flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Exploitation Details
                  {vulnerability.exploitation_successful && (
                    <span className="px-2 py-0.5 rounded text-[10px] bg-green-500/20 text-green-400 border border-green-500/30">
                      CONFIRMED
                    </span>
                  )}
                </h3>
                {expandedSections.exploitation ? <ChevronUp className="w-4 h-4 text-purple-400" /> : <ChevronDown className="w-4 h-4 text-purple-400" />}
              </div>
              {expandedSections.exploitation && (
                <div className="p-4 pt-0 space-y-4">
                  {/* Exploitation Status */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-purple-400 text-xs mb-1">
                        <Target className="w-3 h-3" />
                        Statut
                      </div>
                      <span className={`text-sm font-bold ${vulnerability.exploitation_successful ? 'text-green-400' : 'text-yellow-400'}`}>
                        {vulnerability.exploitation_successful ? '✓ Exploitation Successful' : '⏳ Attempt in progress'}
                      </span>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-purple-400 text-xs mb-1">
                        <Clock className="w-3 h-3" />
                        Date d'exploitation
                      </div>
                      <span className="text-white text-sm">
                        {vulnerability.exploitation_timestamp
                          ? new Date(vulnerability.exploitation_timestamp).toLocaleString('fr-FR')
                          : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Exploit Modules */}
                  {vulnerability.exploit_modules && (
                    <div className="bg-gray-900 rounded-lg p-4">
                      <h4 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
                        <Terminal className="w-4 h-4" />
                        Modules d'Exploitation Disponibles
                      </h4>
                      <div className="space-y-2">
                        {vulnerability.exploit_modules.metasploit?.map((module, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-gray-800 rounded p-2">
                            <code className="text-green-400 text-xs font-mono">{module}</code>
                            <button
                              onClick={() => navigator.clipboard.writeText(module)}
                              className="text-gray-500 hover:text-brand-green p-1"
                              title="Copier"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tool Output / Exploitation Evidence */}
                  {vulnerability.tool_output && (
                    <div>
                      <h4 className="text-sm text-purple-400 font-medium mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Preuve d'Exploitation
                      </h4>
                      <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs max-h-64 overflow-y-auto border border-purple-500/20">
                        <pre className="whitespace-pre-wrap text-green-400">{vulnerability.tool_output}</pre>
                      </div>
                    </div>
                  )}

                  {/* Impact Warning */}
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <h4 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Impact de l'Exploitation
                    </h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">⚠</span>
                        System access CONFIRMED - Potential access level: SYSTEM/Root
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">⚠</span>
                        Lateral movement possible to other systems on the network
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">⚠</span>
                        Sensitive data exfiltration at risk
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Proof of Concept Section */}
          {vulnerability.proof_of_concept?.narrative && (
            <div className="bg-brand-black rounded-xl border border-cyan-500/40 overflow-hidden">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/50 bg-cyan-500/5"
                onClick={() => toggleSection('poc')}
              >
                <h3 className="text-cyan-400 font-bold flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  {t('vulnerabilities.validation.poc_title')}
                  {vulnerability.proof_of_concept?.confidence && (
                    <span className="px-2 py-0.5 rounded text-[10px] bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                      {Math.round(vulnerability.proof_of_concept.confidence * 100)}%
                    </span>
                  )}
                </h3>
                {expandedSections.poc ? <ChevronUp className="w-4 h-4 text-cyan-400" /> : <ChevronDown className="w-4 h-4 text-cyan-400" />}
              </div>
              {expandedSections.poc && (
                <div className="p-4 pt-0 space-y-3">
                  {/* Narrative */}
                  <div className="bg-gray-900 rounded-lg p-4 border border-cyan-500/20">
                    <h4 className="text-sm text-cyan-400 font-medium mb-2 flex items-center gap-2">
                      <FileText className="w-3 h-3" />
                      {t('vulnerabilities.validation.narrative')}
                    </h4>
                    <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {vulnerability.proof_of_concept.narrative}
                    </p>
                  </div>

                  {/* Exploit Path */}
                  {vulnerability.proof_of_concept.exploit_path && (
                    <div className="bg-gray-900 rounded-lg p-4 border border-cyan-500/20">
                      <h4 className="text-sm text-cyan-400 font-medium mb-2 flex items-center gap-2">
                        <Target className="w-3 h-3" />
                        {t('vulnerabilities.validation.exploit_path')}
                      </h4>
                      <p className="text-sm text-gray-300 font-mono">{vulnerability.proof_of_concept.exploit_path}</p>
                    </div>
                  )}

                  {/* Prerequisites */}
                  {vulnerability.proof_of_concept.prerequisites?.length > 0 && (
                    <div className="bg-gray-900 rounded-lg p-4 border border-cyan-500/20">
                      <h4 className="text-sm text-cyan-400 font-medium mb-2 flex items-center gap-2">
                        <Lock className="w-3 h-3" />
                        {t('vulnerabilities.validation.prerequisites')}
                      </h4>
                      <ul className="text-sm text-gray-300 space-y-1">
                        {vulnerability.proof_of_concept.prerequisites.map((prereq, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-cyan-400 mt-0.5">•</span>
                            {prereq}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Details Section */}
          <div className="bg-brand-black rounded-xl border border-gray-800 overflow-hidden">
            <div
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/50"
              onClick={() => toggleSection('details')}
            >
              <h3 className="text-brand-green font-bold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Description & Risques
              </h3>
              {expandedSections.details ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>
            {expandedSections.details && (
              <div className="p-4 pt-0 space-y-4">
                <p className="text-gray-300 text-sm leading-relaxed break-all">
                  {vulnerability.description || 'No description available.'}
                </p>

                {/* Risk Impact */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h4 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                    <Bug className="w-4 h-4" />
                    Impact Potentiel
                  </h4>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-0.5">-</span>
                      System or service compromise
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-0.5">-</span>
                      Unauthorized access to sensitive data
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-0.5">-</span>
                      Execution de code arbitraire possible
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 mt-0.5">-</span>
                      Lateral movement on the network
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Attack Details Section */}
          <div className="bg-brand-black rounded-xl border border-gray-800 overflow-hidden">
            <div
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/50"
              onClick={() => toggleSection('attack')}
            >
              <h3 className="text-brand-green font-bold flex items-center gap-2">
                <Target className="w-4 h-4" />
                Attack Details (CVSS)
              </h3>
              {expandedSections.attack ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>
            {expandedSections.attack && (
              <div className="p-4 pt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <span className="text-xs text-gray-500 block mb-1">Vecteur d'attaque</span>
                    <span className="text-white text-sm font-medium">{attackDetails.vector}</span>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <span className="text-xs text-gray-500 block mb-1">Complexity</span>
                    <span className={`text-sm font-medium ${attackDetails.complexity === 'Low' ? 'text-red-400' : 'text-yellow-400'}`}>
                      {attackDetails.complexity}
                    </span>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <span className="text-xs text-gray-500 block mb-1">Privileges required</span>
                    <span className={`text-sm font-medium ${attackDetails.privileges === 'None' ? 'text-red-400' : 'text-yellow-400'}`}>
                      {attackDetails.privileges}
                    </span>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <span className="text-xs text-gray-500 block mb-1">Interaction utilisateur</span>
                    <span className="text-white text-sm font-medium">{attackDetails.interaction}</span>
                  </div>
                </div>

                {/* Impact Metrics */}
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm text-gray-400 font-medium">Impact</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-28">Confidentiality</span>
                      <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div className={`h-full ${attackDetails.impact_confidentiality === 'High' ? 'bg-red-500 w-full' : attackDetails.impact_confidentiality === 'Low' ? 'bg-yellow-500 w-1/3' : 'bg-gray-600 w-0'}`}></div>
                      </div>
                      <span className="text-xs text-white w-12">{attackDetails.impact_confidentiality}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-28">Integrity</span>
                      <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div className={`h-full ${attackDetails.impact_integrity === 'High' ? 'bg-red-500 w-full' : attackDetails.impact_integrity === 'Low' ? 'bg-yellow-500 w-1/3' : 'bg-gray-600 w-0'}`}></div>
                      </div>
                      <span className="text-xs text-white w-12">{attackDetails.impact_integrity}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-28">Availability</span>
                      <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div className={`h-full ${attackDetails.impact_availability === 'High' ? 'bg-red-500 w-full' : attackDetails.impact_availability === 'Low' ? 'bg-yellow-500 w-1/3' : 'bg-gray-600 w-0'}`}></div>
                      </div>
                      <span className="text-xs text-white w-12">{attackDetails.impact_availability}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Proof/Evidence Section */}
          <div className="bg-brand-black rounded-xl border border-gray-800 overflow-hidden">
            <div
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/50"
              onClick={() => toggleSection('proof')}
            >
              <h3 className="text-brand-green font-bold flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Preuve & Capture
              </h3>
              {expandedSections.proof ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>
            {expandedSections.proof && (
              <div className="p-4 pt-0 space-y-4">
                {/* Tool & Command */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Terminal className="w-4 h-4 text-brand-green" />
                    <span className="text-gray-400">Tool used:</span>
                    <span className="text-white font-medium">{proofData.tool_used}</span>
                  </div>

                  {/* Command used */}
                  <div className="bg-gray-900 rounded-lg p-3 font-mono text-xs">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-500">$ Command</span>
                      <button
                        onClick={() => copyToClipboard(proofData.command)}
                        className="text-gray-500 hover:text-brand-green"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <code className="text-green-400">{proofData.command}</code>
                  </div>
                </div>

                {/* Output/Evidence */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Output / Evidence</span>
                    <span className="text-xs text-gray-500">{new Date(proofData.timestamp).toLocaleString('fr-FR')}</span>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs text-green-400 max-h-48 overflow-y-auto">
                    <pre className="whitespace-pre-wrap">{proofData.output}</pre>
                  </div>
                </div>

                {/* Screenshot if available */}
                {proofData.screenshot && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Image className="w-4 h-4 text-brand-green" />
                      <span className="text-sm text-gray-400">Screenshot</span>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-2 border border-gray-800">
                      <img
                        src={proofData.screenshot}
                        alt="Proof screenshot"
                        className="w-full rounded"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Solution Section */}
          <div className="bg-brand-black rounded-xl border border-brand-green/50 overflow-hidden">
            <div
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-800/50 bg-brand-green/10"
              onClick={() => toggleSection('solution')}
            >
              <h3 className="text-brand-green font-bold flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Solution & Remediation
              </h3>
              {expandedSections.solution ? <ChevronUp className="w-4 h-4 text-brand-green" /> : <ChevronDown className="w-4 h-4 text-brand-green" />}
            </div>
            {expandedSections.solution && (
              <div className="p-4 pt-0 space-y-4">
                {/* Main Recommendation */}
                <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-4">
                  <h4 className="text-brand-green font-medium mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Recommandation principale
                  </h4>
                  <p className="text-gray-300 text-sm">{solution.description}</p>
                </div>

                {/* Steps */}
                <div>
                  <h4 className="text-sm text-gray-400 font-medium mb-3">Remediation steps</h4>
                  <ol className="space-y-2">
                    {solution.steps.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span className="text-gray-300 pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Workaround */}
                {solution.workaround && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <h4 className="text-yellow-400 font-medium mb-2 flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Solution temporaire (Workaround)
                    </h4>
                    <p className="text-gray-300 text-sm">{solution.workaround}</p>
                  </div>
                )}

                {/* References */}
                <div>
                  <h4 className="text-sm text-gray-400 font-medium mb-2">References</h4>
                  <div className="space-y-2">
                    {solution.references.map((ref, idx) => (
                      <a
                        key={idx}
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-brand-green hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {ref.title}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-800 bg-brand-black flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Fermer
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.open(`/vulnerabilities/${vulnerability.id}`, '_blank')}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              View full details
            </button>
            <button
              className="px-4 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 font-medium flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exporter
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default VulnerabilitySlider;
